import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FilePlus, RefreshCw } from "lucide-react";
import { createManualLead, SETTORI, PROVINCE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const EMPTY = { ragione_sociale: "", email: "", telefono: "", settore: "", provincia: "", citta: "", indirizzo: "", cap: "" };

export const NuovoPreventivoDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [nf, setNf] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const setNfField = (k) => (v) => setNf((s) => ({ ...s, [k]: v }));

  const createPreventivo = async () => {
    if (!nf.ragione_sociale.trim()) { toast.error("Inserisci la ragione sociale del cliente"); return; }
    setCreating(true);
    try {
      const { lead_id } = await createManualLead(nf);
      toast.success("Cliente creato — apri il preventivo");
      onOpenChange(false);
      setNf(EMPTY);
      navigate(`/admin/leads/${lead_id}?preventivo=1`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Creazione non riuscita");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-navy-800 border-white/10 text-white max-w-lg" data-testid="new-preventivo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><FilePlus className="h-5 w-5 text-gold-400" /> Nuovo preventivo fotovoltaico</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-slate-400 text-sm -mt-2">Crea un'offerta senza bollette: inserisci i dati del cliente, poi comporrai le voci del preventivo e scaricherai il PDF.</DialogDescription>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <NField label="Ragione sociale *" value={nf.ragione_sociale} onChange={setNfField("ragione_sociale")} testid="nf-ragione" />
          <NField label="Email" value={nf.email} onChange={setNfField("email")} testid="nf-email" />
          <NField label="Telefono" value={nf.telefono} onChange={setNfField("telefono")} testid="nf-telefono" />
          <div>
            <Label className="text-slate-400 text-xs mb-1 block">Settore</Label>
            <select value={nf.settore} onChange={(e) => setNfField("settore")(e.target.value)} className="input-dark h-10 w-full rounded-md px-3" data-testid="nf-settore">
              <option value="">—</option>
              {SETTORI.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-slate-400 text-xs mb-1 block">Provincia</Label>
            <select value={nf.provincia} onChange={(e) => setNfField("provincia")(e.target.value)} className="input-dark h-10 w-full rounded-md px-3" data-testid="nf-provincia">
              <option value="">—</option>
              {PROVINCE.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <NField label="Città / Comune" value={nf.citta} onChange={setNfField("citta")} testid="nf-citta" />
          <NField label="Indirizzo (via, civico)" value={nf.indirizzo} onChange={setNfField("indirizzo")} testid="nf-indirizzo" />
          <NField label="CAP" value={nf.cap} onChange={setNfField("cap")} testid="nf-cap" />
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg border-white/15 bg-transparent text-slate-200 hover:bg-white/5" data-testid="nf-cancel">Annulla</Button>
          <Button onClick={createPreventivo} disabled={creating} className="rounded-lg bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold" data-testid="nf-create">
            {creating ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <FilePlus className="h-4 w-4 mr-1.5" />} Crea e apri preventivo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NField = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-slate-400 text-xs mb-1 block">{label}</Label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} className="input-dark h-10" data-testid={testid} />
  </div>
);
